/**
 * PaymentsPage.tsx
 *
 * Practice-wide payments ledger for the dedicated All Payments route.
 * Supports filtering via sidebar, detail modals, void, and refund actions.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { PatientLink } from '@/components/ui/PatientLink';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Gift, Megaphone, Plus } from 'lucide-react';
import {
  getPayments,
  getPaymentStats,
  voidPayment,
  refundPaymentTransaction,
  type Payment,
} from '@/services/payments.service';
import { listUsers } from '@/services/admin.service';
import { usePaymentFilterOptions } from '@/hooks/filters';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PaymentStatusBadge } from '@/components/payments/PaymentStatusBadge';
import { StatTile } from '@/components/payments/StatTile';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { CollectPaymentModal } from '@/pages/payments/CollectPaymentModal';
import { RecordPatientPaymentModal } from '@/pages/payments/RecordPatientPaymentModal';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { appShareHint, buildPaymentFilterConfigs, formatCalendarDate, formatCurrency, formatPaymentDate } from '@/pages/payments/paymentsShared';
import { buildApiParams, buildMarketingFilterConditions, extractSearch } from '@/lib/filterRegistry';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { useAuth } from '@/hooks/useAuth';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';
import { useIsBelow } from '@/hooks/useBreakpoint';
import {
  columnsForContainerWidth,
  type ColumnWidthThresholds,
} from '@/components/ui/responsiveColumns';
import { getTimezoneAbbreviation, toLocaleDateStringInTz, toLocaleTimeStringInTz } from '@/lib/facilityTimezone';


const PER_PAGE = 100;

/** Where Payments switches to cards. Passed to DataTable as well, so the two agree. */
const PAYMENTS_CARD_BREAKPOINT = 'md' as const;

/** Removal order, widest threshold first. Unchanged from the inline chain it replaces. */
const PAYMENT_COLUMN_THRESHOLDS: ColumnWidthThresholds = [
  [900, 'strMemo'],
  [800, 'userID'],
  [700, 'entryDate'],
  [600, 'strPayMethod'],
  [500, 'strPayType'],
];

function CompactDate({ value, tz }: { value: string | null | undefined; tz: string }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const d = new Date(value);
  if (isNaN(d.getTime())) return <span className="text-xs">{value}</span>;
  const dateLine = toLocaleDateStringInTz(d, 'en-US', { month: 'short', day: 'numeric', year: '2-digit', timeZone: tz });
  const time = toLocaleTimeStringInTz(d, 'en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
  const tzAbbr = getTimezoneAbbreviation(tz, d);
  return (
    <span className="flex flex-col items-start leading-tight whitespace-nowrap">
      <span className="text-[11px]">{dateLine}</span>
      <span className="text-[10px] text-slate-400">{time} {tzAbbr}</span>
    </span>
  );
}

/**
 * The Date column.
 *
 * Reads `entryDate` — when the payment was actually taken — rather than the
 * `dtmDate` business-date column. entryDate is a true instant, so the date and
 * the time beneath it are one consistent conversion into the viewer's zone.
 *
 * This used to render from the API's collapsed `displayDate` (entryDate when the
 * two columns land on the same day, dtmDate otherwise). Whenever they diverged
 * that produced a timezone-shifted bare date over a meaningless "12:00 AM".
 *
 * Legacy rows with no entryDate fall back to the dtmDate calendar date, read
 * literally — a bare DATE must never be pushed through a timezone.
 */
function PaymentDateCell({ row, tz }: { row: Payment; tz: string }) {
  if (row.entryDate) return <CompactDate value={row.entryDate} tz={tz} />;
  return (
    <span className="text-[11px]">
      {formatCalendarDate(row.dtmDate, { month: 'short', day: 'numeric', year: '2-digit' })}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PaymentsPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const userTz = useUserTimezone();
  const updateAIContext = usePageContext('Payments');

  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const filterConfigs = useMemo(
    // The one caller that wants a default date range -- see the options type.
    () => buildPaymentFilterConfigs(doctorLabel, coordLabel, { defaultDatePreset: 'today' }),
    [doctorLabel, coordLabel],
  );

  const filterState = useLocalDashboardFilters(filterConfigs, 'payments');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const filters = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );
  const searchTerm = extractSearch(effectiveValues);

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(direction);
    setPage(1);
  }, []);

  const [voidTarget, setVoidTarget] = useState<Payment | null>(null);
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  const [detailPayment, setDetailPayment] = useState<Payment | null>(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'payments', entityLabel: 'payments' });

  useEffect(() => { setPage(1); }, [filters]);

  const marketingFilterConditions = useMemo(
    () => buildMarketingFilterConditions('payment', effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  // ─── Queries ──────────────────────────────────────────────────────────

  const {
    data: paymentsData,
    isLoading: paymentsLoading,
  } = useQuery({
    queryKey: ['payments', filters, page, sortBy, sortDir],
    queryFn: () => getPayments(filters, { page, pageSize: PER_PAGE, sortBy: sortBy || undefined, sortDir: sortBy ? sortDir : undefined }),
    staleTime: 30_000,
  });

  // `isPending` is exactly "no figures for the CURRENT filters yet", because a
  // filter change swaps the query key and clears data — so the tiles show
  // spinners when the numbers genuinely do not exist, and keep showing real
  // values through a background refetch of the same key rather than flickering.
  const {
    data: paymentStats,
    isPending: statsPending,
    isError: statsFailed,
  } = useQuery({
    queryKey: ['payments-stats', filters],
    queryFn: () => getPaymentStats(filters),
    staleTime: 60_000,
  });

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60_000,
  });

  const sidebarOptions = usePaymentFilterOptions();

  // ─── Mutations ────────────────────────────────────────────────────────

  const voidMutation = useMutation({
    mutationFn: (id: number) => voidPayment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setVoidTarget(null);
    },
  });

  const refundMutation = useMutation({
    mutationFn: ({ transactionId, amount }: { transactionId: number; amount: number }) =>
      refundPaymentTransaction(transactionId, { amount }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setRefundTarget(null);
    },
  });

  // ─── Derived data ─────────────────────────────────────────────────────

  const payments = paymentsData?.data ?? [];
  const totalPages = paymentsData ? Math.ceil((paymentsData.meta.total ?? 0) / PER_PAGE) : 1;

  /** Map user IDs to display names for the User / Voided By columns */
  const userMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of users ?? []) {
      map.set(u.userId, `${u.firstName} ${u.lastName}`.trim() || u.userName);
    }
    return map;
  }, [users]);

  // Client-side search filtering (patient name / ref number / patient ID)
  const filteredPayments = useMemo(() => {
    if (!searchTerm.trim()) return payments;
    const term = searchTerm.toLowerCase();
    return payments.filter((pmt) => {
      const ref = (pmt.strRefNumber ?? '').toLowerCase();
      const patId = String(pmt.patientID ?? '');
      const firstName = (pmt.patientFirstName ?? '').toLowerCase();
      const lastName = (pmt.patientLastName ?? '').toLowerCase();
      const fullName = `${firstName} ${lastName}`;
      return (
        ref.includes(term) ||
        patId.includes(term) ||
        firstName.includes(term) ||
        lastName.includes(term) ||
        fullName.includes(term)
      );
    });
  }, [payments, searchTerm]);

  function handleExport() {
    startExport(filters);
  }


  // ─── AI Page Context ──────────────────────────────────────────────────
  const activeFilters = useMemo(
    () =>
      Object.entries(effectiveValues)
        .filter(([key, val]) => {
          if (key === 'search') return false;
          if (Array.isArray(val)) return val.length > 0;
          if (typeof val === 'object' && val !== null && 'from' in val) return !!(val.from || val.to);
          return false;
        })
        .map(([key]) => key)
        .join(', '),
    [effectiveValues],
  );

  useEffect(() => {
    const parts: string[] = [];
    if (activeFilters) parts.push(activeFilters);
    if (searchTerm) parts.push(`search="${searchTerm}"`);

    updateAIContext({
      filters: parts.length > 0 ? parts.join(', ') : 'no active filters',
      dataSummary: `${filteredPayments.length} shown of ${paymentsData?.meta.total ?? 0} total, page ${page}/${totalPages}`,
    });
  }, [activeFilters, searchTerm, filteredPayments.length, paymentsData?.meta.total, page, totalPages, updateAIContext]);

  // ─── DataTable columns: Payments ──────────────────────────────────────

  const allPaymentColumns = useMemo<DataTableColumn<Payment>[]>(() => [
    {
      key: 'dtmDate',
      header: 'Date',
      // The constraint is the SECOND line, not the date: "12:00 AM CDT" runs
      // ~67px in Inter at 10px. The table is `tableFixed` with
      // `[&_td]:overflow-hidden`, so this width is enforced rather than a hint —
      // at the old 78px the content box was 46px and the zone abbreviation lost
      // its last letter ("CD…") at every viewport.
      //
      // px-3 overrides the cell's px-4 via twMerge, which is what makes the time
      // line fit inside 88px: 64px of content instead of 56px. Widen before
      // restoring px-4.
      width: 'w-[88px] px-3 whitespace-nowrap',
      sortable: true,
      render: (row) => <PaymentDateCell row={row} tz={userTz} />,
    },
    {
      key: 'patientID',
      header: 'Patient',
      width: 'min-w-[160px]',
      render: (row) =>
        row.patientID ? (
          <PatientLink
            patientId={row.patientID}
            patientName={row.patientFirstName || row.patientLastName
              ? `${row.patientFirstName ?? ''} ${row.patientLastName ?? ''}`.trim()
              : `#${row.patientID}`}
            className="text-blue-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.patientFirstName || row.patientLastName
              ? `${row.patientFirstName ?? ''} ${row.patientLastName ?? ''}`.trim()
              : `#${row.patientID}`}
          </PatientLink>
        ) : (
          <span>—</span>
        ),
    },
    {
      key: 'curAmount',
      header: 'Amount',
      width: 'w-[85px] whitespace-nowrap',
      sortable: true,
      // Show what was charged, not the raw stored amount — on rows whose
      // curAmount is gross of the gift card, the raw figure double-counts the
      // card that is already itemised underneath it.
      render: (row) => (
        <span className="flex flex-col">
          <span className="font-medium text-slate-800">
            {formatCurrency(row.cardChargedAmount ?? row.curAmount)}
          </span>
          {(row.giftCardTotal ?? 0) > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-purple-600">
              <Gift className="h-3 w-3 shrink-0" />
              {formatCurrency(row.giftCardTotal!)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'strPayType',
      header: 'Type',
      render: (row) => {
        const charged = row.cardChargedAmount ?? row.curAmount;
        if (charged === 0 && (row.giftCardTotal ?? 0) > 0) return <span className="text-xs">Gift Card</span>;
        if (charged === 0) return <span className="text-xs">Comp</span>;
        return <span className="capitalize text-xs">{row.strPayType ?? '—'}</span>;
      },
    },
    {
      key: 'strPayMethod',
      header: 'Method',
      render: (row) => {
        const charged = row.cardChargedAmount ?? row.curAmount;
        if (charged === 0 && (row.giftCardTotal ?? 0) > 0) return <span className="text-xs">Gift Card</span>;
        if (charged === 0) return <span className="text-xs">—</span>;
        return <span className="capitalize text-xs">{row.strPayMethod ?? '—'}</span>;
      },
    },
    {
      key: 'facilityName',
      header: 'Facility',
      render: (row) => <span className="text-xs text-slate-500">{row.facilityName ?? '—'}</span>,
    },
    {
      key: 'materialsText',
      header: 'Materials',
      render: (row) => (
        <span className="text-xs text-slate-500 line-clamp-3" title={row.materialsText ?? undefined}>
          {row.materialsText ?? '—'}
        </span>
      ),
    },
    {
      key: 'strMemo',
      header: 'Notes',
      render: (row) => <span className="text-xs line-clamp-3" title={row.strMemo ?? undefined}>{row.strMemo ?? '—'}</span>,
    },
    {
      key: 'userID',
      // Named for the sidebar's `users` filter (label 'User'), not for what the
      // column used to be called: a column and the filter that narrows it
      // reading as two different things is what sent people looking for a
      // second filter that does not exist.
      header: 'User',
      render: (row) => <span className="text-xs">{row.userID ? (userMap.get(row.userID) ?? `#${row.userID}`) : '—'}</span>,
    },
    {
      key: 'entryDate',
      header: 'Processed',
      render: (row) => <CompactDate value={row.entryDate} tz={userTz} />,
    },
    {
      key: 'void',
      header: 'Status',
      width: 'w-[80px]',
      render: (row) => {
        const voidUserName = row.voidUserID
          ? userMap.get(row.voidUserID) ?? `#${row.voidUserID}`
          : null;
        return (
          <PaymentStatusBadge
            payment={row}
            voidDate={row.voidDate}
            voidUserName={voidUserName}
          />
        );
      },
    },
  ], [userMap]);

  // Responsive column hiding — remove columns as container shrinks.
  // Always visible: Date, Patient, Amount, Materials, Status
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(Infinity);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTableWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Unconditional and at the top level — see the note in LeadsPage: `useIsBelow` is a
  // useSyncExternalStore hook and a short-circuited call changes the hook count.
  const asCards = useIsBelow(PAYMENTS_CARD_BREAKPOINT);

  // Same defect as Leads, different thresholds (SCRUM-1447). Payments was not in the report
  // only because nobody filed it; at 375px this chain removed Memo, User, Entry Date, Method
  // and Type from the card, which is five of its fields.
  const paymentColumns = useMemo(
    () =>
      columnsForContainerWidth(
        allPaymentColumns,
        tableWidth,
        PAYMENT_COLUMN_THRESHOLDS,
        asCards,
      ),
    [allPaymentColumns, tableWidth, asCards],
  );


  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={filterConfigs}
            values={effectiveValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={filterState.setFilter}
            onNegateChange={filterState.setNegation}
            onClear={filterState.clearFilter}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="payments"
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 p-6">
            <PageHeader
              variant="compact"
              title="All Payments"
              actions={
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={handleExport}
                    disabled={(paymentsData?.meta.total ?? 0) === 0 || exporting}
                    loading={exporting}
                  >
                    Export
                  </Button>
                  {exportModal}
                  {hasPermission(20) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Megaphone className="h-4 w-4" />}
                      onClick={() => setMarketingModalOpen(true)}
                    >
                      Email / SMS List
                    </Button>
                  )}
                  <Button
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => setShowRecordModal(true)}
                  >
                    Collect Payment
                  </Button>
                </div>
              }
            />

            {/*
              One dollar, counted once as cash and once as revenue. A gift card
              SALE is stored value (a liability), not a sale; a gift card
              REDEMPTION is a tender against the product, so the product earns
              the revenue. The old single "Total Payments" figure summed every
              payment event and so counted gift-card dollars twice.

              No `paymentStats &&` gate: the row renders unconditionally and the
              tiles carry their own loading state, so the header keeps a stable
              height instead of collapsing while the figures load.
            */}
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                tone="slate"
                label="Cash Collected"
                loading={statsPending}
                failed={statsFailed}
                value={formatCurrency(paymentStats?.cashCollected ?? paymentStats?.totalAmount)}
                hint="Money received, incl. gift cards sold"
              />
              <StatTile
                tone="emerald"
                label="Revenue Earned"
                loading={statsPending}
                failed={statsFailed}
                value={formatCurrency(paymentStats?.revenueEarned ?? paymentStats?.totalAmount)}
                hint={
                  <>
                    Delivered products &amp; services
                    {paymentStats?.giftCardSold != null && paymentStats.giftCardSold > 0
                      ? ` · −${formatCurrency(paymentStats.giftCardSold)} sold`
                      : ''}
                    {paymentStats?.giftCardRedeemed != null && paymentStats.giftCardRedeemed > 0
                      ? ` · +${formatCurrency(paymentStats.giftCardRedeemed)} redeemed`
                      : ''}
                  </>
                }
              />
              {/*
                Purchased in App — how much of Cash Collected the patient apps
                took, rather than staff entering it here. Read from
                api_request_metadata: a payment with no row there was entered in
                Prospera by a staff member. Counts membership rebills as well as
                product sales, so it is WIDER than the App Orders grid, which
                needs a product line — see PaymentsController::appSourcedSelect().

                Shown as a share of Cash Collected because the number only means
                something next to the total it came out of — $40k of app sales
                reads very differently against $50k than $500k.
              */}
              <StatTile
                tone="sky"
                label="Purchased in App"
                loading={statsPending}
                failed={statsFailed}
                value={formatCurrency(paymentStats?.appPurchased ?? 0)}
                hint={appShareHint(
                  paymentStats?.appPurchased,
                  paymentStats?.cashCollected ?? paymentStats?.totalAmount,
                )}
              />
              <StatTile
                tone="amber"
                label="Gift Card Liability"
                loading={statsPending}
                failed={statsFailed}
                value={formatCurrency(paymentStats?.giftCardLiability ?? 0)}
                hint="Unredeemed, excl. expired (all time)"
              />
            </div>
          </div>

          <div ref={tableContainerRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <DataTable<Payment>
              data={filteredPayments}
              columns={paymentColumns}
              cardBreakpoint={PAYMENTS_CARD_BREAKPOINT}
              keyField="paymentID"
              tableFixed
              className="[&>div]:overflow-x-hidden [&_td]:overflow-hidden [&_td]:break-words"
              loading={paymentsLoading}
              onRowClick={(row) => {
                if (row.patientID) setDetailPayment(row);
              }}
              page={page}
              pageSize={PER_PAGE}
              total={paymentsData?.meta.total ?? 0}
              onPageChange={(p) => setPage(p)}
              manualSort
              sortKey={sortBy || null}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              emptyTitle="No payments found"
              emptyDescription="Try adjusting your filters."
            />
          </div>
        </div>
      </ListPageLayout>

      {/* ── Void Confirmation Dialog ──────────────────────────────────── */}
      <ConfirmDialog
        isOpen={!!voidTarget}
        onClose={() => setVoidTarget(null)}
        onConfirm={async () => {
          if (voidTarget) await voidMutation.mutateAsync(voidTarget.paymentID);
        }}
        title="Void Payment?"
        message={
          <>
            Void payment of <strong>{formatCurrency(voidTarget?.cardChargedAmount ?? voidTarget?.curAmount)}</strong> from{' '}
            {formatPaymentDate(voidTarget, userTz)}? This cannot be undone.
          </>
        }
        confirmLabel="Void Payment"
        variant="danger"
      />

      {/* ── Refund Confirmation Dialog ────────────────────────────────── */}
      <ConfirmDialog
        isOpen={!!refundTarget}
        onClose={() => setRefundTarget(null)}
        onConfirm={async () => {
          if (refundTarget) {
            await refundMutation.mutateAsync({
              transactionId: refundTarget.paymentID,
              // Refund what was actually charged to the card. curAmount is gross
              // of an applied gift card on some payments, so refunding it would
              // attempt to return more than the card was ever charged.
              amount: refundTarget.cardChargedAmount ?? refundTarget.curAmount,
            });
          }
        }}
        title="Refund Payment?"
        message={
          <>
            Process a full refund of{' '}
            <strong>{formatCurrency(refundTarget?.cardChargedAmount ?? refundTarget?.curAmount)}</strong> from{' '}
            {formatPaymentDate(refundTarget, userTz)}? The refund will be submitted to the payment processor.
          </>
        }
        confirmLabel="Process Refund"
        variant="warning"
      />

      {/*
       * Payment detail modal — uses the same unified RecordPatientPaymentModal
       * that the patient's Payments tab renders, so the main All-Payments list
       * and the per-patient list share one edit/view surface.
       */}
      {detailPayment?.patientID ? (
        <RecordPatientPaymentModal
          patientId={detailPayment.patientID}
          paymentId={detailPayment.paymentID}
          open={!!detailPayment}
          onClose={() => setDetailPayment(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['payments'] });
            void queryClient.invalidateQueries({ queryKey: ['payments-stats'] });
          }}
        />
      ) : null}

      <CollectPaymentModal
        isOpen={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        onSuccess={() => {
          setShowRecordModal(false);
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          queryClient.invalidateQueries({ queryKey: ['payments-stats'] });
        }}
      />

      {hasPermission(20) && (
        <MarketingActionsModal
          isOpen={marketingModalOpen}
          onClose={() => setMarketingModalOpen(false)}
          sourceType="payment"
          filterConditions={marketingFilterConditions}
        />
      )}
    </>
  );
}
